import React, { useState } from "react";
import Image from "next/image";

interface TestimonialTimelineItem {
  year: string;
  name: string;
  achievement: string;
  rank: string;
  image: string;
  quote: string;
}

const timelineItems: TestimonialTimelineItem[] = [
  {
    year: "2078",
    name: "Nirajan Shah",
    achievement: "IOE Rank 1",
    rank: "01",
    image: "/img/testimonial/NirajanShah.jpg",
    quote:
      "Clamphook sharpened my daily consistency with focused mock tests and strong mentor support. Stay punctual, honest, and enjoy the process.",
  },
  {
    year: "IOE MSc",
    name: "Er. Sandip Gewali",
    achievement: "IOE MSc Rank 1",
    image: "/img/testimonial/SandipGewali.jpg",
    quote:
      "A crucial part of my entrance journey. Immense gratitude to the whole team for inspiration and wonderful tutoring. Delighted to have chosen Clamphook.",
  },
  {
    year: "2078",
    name: "Sahadev Chaulagain",
    achievement: "IOE Rank 5",
    rank: "05",
    image: "/img/testimonial/Sahadev.jpg",
    quote:
      "The guidance and structure gave me clarity in preparation. I owe a lot to the Clamphook family for this journey.",
  },
  {
    year: "IOE MSc",
    name: "Er. Sunil Sharma",
    achievement: "IOE MSc Rank 2",
    image: "/img/testimonial/SunilSharma.jpg",
    quote:
      "I am thankful for the live classes, recorded classes and QAD sessions on Clamphook. Very satisfied with the quality.",
  },
  {
    year: "2078",
    name: "Andis Poudel",
    achievement: "IOE Rank 6",
    rank: "06",
    image: "/img/testimonial/Andis.jpg",
    quote:
      "From lectures to revision strategy, everything felt practical and exam-focused. It made my preparation much more efficient.",
  },
  {
    year: "IOE MSc",
    name: "Er. Salim Maharjan",
    achievement: "IOE MSc Rank 9",
    image: "/img/testimonial/SalimMaharjan.jpg",
    quote:
      "Clamphook is a new face in entrance preparation, run by youths who understand what it takes to crack competitive exams.",
  },
  {
    year: "2078",
    name: "Saksham Sapkota",
    achievement: "IOE Rank 10",
    rank: "10",
    image: "/img/testimonial/Saksham.jpg",
    quote:
      "The classes and practice flow helped me stay on track every week. The momentum and confidence built up steadily.",
  },
  {
    year: "2078",
    name: "Mukesh Dahal",
    achievement: "IOE Rank 13",
    image: "/img/testimonial/MukeshDahal.jpg",
    quote:
      "Clamphook made the journey much easier. I highly recommend it for all upcoming IOE aspirants.",
  },
];

const TestimonialsTimeline: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const previewItems = timelineItems.slice(
    0,
    Math.ceil(timelineItems.length / 2),
  );

  const renderGrid = (items: TestimonialTimelineItem[]) => (
    <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-gray-200">
      {items.map((item) => (
        <article
          key={`${item.name}-${item.year}`}
          className="relative border-r border-b border-gray-200 p-6 md:p-8"
        >
          <div
            className="absolute top-4 right-6 text-6xl md:text-7xl font-black text-primary-100 leading-none select-none"
            style={{ fontVariantNumeric: "tabular-nums" }}
            aria-hidden="true"
          >
            {item.rank}
          </div>

          <div className="relative flex items-start gap-4">
            <Image
              src={item.image}
              alt={item.name}
              width={112}
              height={112}
              sizes="112px"
              loading="lazy"
              className="h-14 w-14 object-cover border border-gray-200"
            />

            <div className="min-w-0">
              <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-500">
                Batch {item.year}
              </span>
              <h3 className="mt-1 text-lg font-bold text-gray-900 leading-tight">
                {item.name}
              </h3>
              <p className="text-sm font-semibold text-primary-700">
                {item.achievement}
              </p>
            </div>
          </div>

          <blockquote className="relative mt-4 text-sm md:text-base text-gray-700 leading-7">
            &ldquo;{item.quote}&rdquo;
          </blockquote>
        </article>
      ))}
    </div>
  );

  return (
    <section className="bg-transparent py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-10 md:mb-14">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">
            Results
          </p>
          <h2 className="mt-2 text-3xl md:text-4xl font-black text-gray-900 tracking-tight">
            What Our Toppers Say
          </h2>
          <p className="mt-3 text-gray-600 text-sm md:text-base max-w-2xl">
            Real student voices from Clamphook results and preparation journeys.
          </p>
        </div>

        {renderGrid(previewItems)}

        {timelineItems.length > previewItems.length && (
          <div className="mt-8">
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center border border-primary-900 px-5 py-3 text-sm font-semibold text-primary-900 transition-colors hover:bg-primary-900 hover:text-white rounded-xl"
            >
              Show More Testimonials
            </button>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[120] bg-black/55 backdrop-blur-sm p-4 sm:p-6">
          <div className="mx-auto flex h-full max-w-6xl flex-col border border-gray-200 bg-white rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 sm:px-6">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                  All Testimonials
                </h3>
                <p className="text-sm text-gray-600">
                  Complete student timeline and feedback
                </p>
              </div>

              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-xl"
              >
                Close
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
              {renderGrid(timelineItems)}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default TestimonialsTimeline;
